import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { auth } from '../../config/firebaseConfig';
import {
  getRedirectResult,
  GoogleAuthProvider,
  linkWithPopup,
  linkWithRedirect,
  onAuthStateChanged,
  unlink
} from 'firebase/auth';
import {
  Menu, ChevronLeft, Link2, Unlink, Calendar, CheckCircle, AlertCircle,
  UserCog, Bell, Search, UserPlus, UserCheck, UserX, X,
} from 'lucide-react';
import CustomAlertModal from '../modals/CustomAlertModal';
import MobilePersonalInfoEdit from './MobilePersonalInfoEdit';
import './styles/MobileCalendarView.css';
import { API_BASE_URL } from '../../config/apiConfig';
import { shouldFallbackToRedirect, shouldUseRedirectForGoogleAuth } from '../../utils/authBrowser';

const GOOGLE_LINK_REDIRECT_PENDING_KEY = 'pendingGoogleLinkRedirect';

// ── 친구 섹션 헬퍼 ──────────────────────────────────────────
const Avatar = ({ user, size = 40 }) => {
  const initial = (user.firstName?.[0] || user.email?.[0] || '?').toUpperCase();
  if (user.profileImage) {
    return (
      <img
        src={user.profileImage}
        alt={initial}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: '#dbeafe',
      color: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.4, flexShrink: 0,
    }}>
      {initial}
    </div>
  );
};

const UserName = ({ user }) => (
  <span>{user.firstName}</span>
);


const FriendSpinner = () => (
  <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
);

const FriendEmpty = ({ icon, text, sub }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', gap: '12px' }}>
    {icon}
    <p style={{ margin: 0, color: '#64748b', fontSize: '15px', fontWeight: 500 }}>{text}</p>
    {sub && <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px' }}>{sub}</p>}
  </div>
);

const fListItem = { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px' };
const fIconBtn = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const fCenter = { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px' };

async function friendApi(path, method = 'GET', body) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

// ── 메인 컴포넌트 ──────────────────────────────────────────
const MobileSettings = ({ user, installPrompt, onInstallPromptUsed }) => {
  const navigate = useNavigate();

  // 섹션 토글
  const [settingsSection, setSettingsSection] = useState('settings'); // 'settings' | 'friends'

  // ── 기존 설정 상태 ──
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isGoogleLinked, setIsGoogleLinked] = useState(false);
  const [isCalendarLinked, setIsCalendarLinked] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', type: 'warning', onConfirm: null, confirmText: '확인' });
  const [showPersonalInfo, setShowPersonalInfo] = useState(false);
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [reminderMinutesList, setReminderMinutesList] = useState([10]);
  const [isSavingNotif, setIsSavingNotif] = useState(false);
  const [customMin, setCustomMin] = useState('');
  const [customUnit, setCustomUnit] = useState('분');

  // ── 친구 섹션 상태 ──
  const [friendTab, setFriendTab] = useState('friends'); // friends | search
  const [friends, setFriends] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pendingActions, setPendingActions] = useState({});
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const formatMin = (m) => {
    if (m < 60) return `${m}분`;
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return rem === 0 ? `${h}시간` : `${h}시간 ${rem}분`;
  };

  const addCustomMin = () => {
    const raw = parseInt(customMin, 10);
    if (isNaN(raw) || raw < 1) return;
    const mins = customUnit === '시간' ? raw * 60 : raw;
    if (mins > 2880) return;
    setReminderMinutesList(prev =>
      prev.includes(mins) ? prev : [...prev, mins].sort((a, b) => a - b)
    );
    setCustomMin('');
  };

  // ── 기존 설정 effects ──
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        const hasGoogleProvider = firebaseUser.providerData?.some(p => p.providerId === 'google.com');
        setIsGoogleLinked(prev => prev || !!hasGoogleProvider);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      const hasGoogleProvider = auth.currentUser?.providerData?.some(p => p.providerId === 'google.com');
      const hasGoogleInDB = !!user.google?.id;
      setIsGoogleLinked(!!hasGoogleProvider || hasGoogleInDB);
      setIsCalendarLinked(!!user.google?.refreshToken);
    }
  }, [user]);

  const showAlert = useCallback((title, message, type = 'info') => {
    setAlertModal({ isOpen: true, title, message, type });
  }, []);

  const completeGoogleLink = useCallback(async (firebaseUser = auth.currentUser) => {
    if (!firebaseUser) throw new Error('Firebase user is not available.');

    const googleProviderData = firebaseUser.providerData.find(p => p.providerId === 'google.com');
    const googleId = googleProviderData?.uid;
    const googleEmail = googleProviderData?.email;
    const token = await firebaseUser.getIdToken();
    const linkRes = await fetch(`${API_BASE_URL}/api/auth/link-google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ googleId, googleEmail })
    });
    if (!linkRes.ok) {
      const data = await linkRes.json().catch(() => ({}));
      throw new Error(data.msg || 'Google account link failed.');
    }

    setIsGoogleLinked(true);
    localStorage.setItem('loginMethod', 'google');
    localStorage.setItem('googleConnected', 'true');
    window.dispatchEvent(new Event('userProfileUpdated'));

    try {
      const returnUrl = encodeURIComponent('/settings');
      const calendarRes = await fetch(`${API_BASE_URL}/api/auth/google/calendar-consent?returnUrl=${returnUrl}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const calendarData = await calendarRes.json();
      if (calendarData.url) {
        localStorage.setItem('pendingCalendarSync', 'true');
        window.location.href = calendarData.url;
        return;
      }
    } catch (calErr) {
      console.warn('[MobileSettings] Calendar consent request failed:', calErr);
    }

    showAlert('연동 완료', '구글 계정이 연동되었습니다. 캘린더 연동은 아래 버튼을 눌러주세요.', 'success');
  }, [showAlert]);

  useEffect(() => {
    if (localStorage.getItem(GOOGLE_LINK_REDIRECT_PENDING_KEY) !== 'true') return undefined;

    let cancelled = false;
    setIsLinking(true);
    getRedirectResult(auth)
      .then(result => {
        if (cancelled) return;
        localStorage.removeItem(GOOGLE_LINK_REDIRECT_PENDING_KEY);
        if (result?.user) return completeGoogleLink(result.user);
      })
      .catch(error => {
        if (cancelled) return;
        localStorage.removeItem(GOOGLE_LINK_REDIRECT_PENDING_KEY);
        showAlert('연동 실패', `오류: ${error.message}`, 'error');
      })
      .finally(() => {
        if (!cancelled) setIsLinking(false);
      });

    return () => { cancelled = true; };
  }, [completeGoogleLink, showAlert]);

  const handleLinkGoogle = async () => {
    setIsLinking(true);
    const switchProvider = new GoogleAuthProvider();
    switchProvider.setCustomParameters({ prompt: 'select_account' });

    try {
      if (shouldUseRedirectForGoogleAuth()) {
        localStorage.setItem(GOOGLE_LINK_REDIRECT_PENDING_KEY, 'true');
        await linkWithRedirect(auth.currentUser, switchProvider);
        return;
      }

      const result = await linkWithPopup(auth.currentUser, switchProvider);
      await completeGoogleLink(result.user);
    } catch (error) {
      if (error.code === 'auth/credential-already-in-use') {
        showAlert('연동 실패', '이 구글 계정은 이미 다른 계정에 연결되어 있습니다.', 'error');
      } else if (error.code === 'auth/provider-already-linked') {
        setIsGoogleLinked(true);
        showAlert('연동 완료', '구글 계정이 이미 연동되어 있습니다.', 'success');
      } else if (error.code === 'auth/popup-closed-by-user' && !shouldFallbackToRedirect(error)) {
        // 사용자가 직접 닫은 경우는 조용히 유지합니다.
      } else if (shouldFallbackToRedirect(error)) {
        try {
          localStorage.setItem(GOOGLE_LINK_REDIRECT_PENDING_KEY, 'true');
          await linkWithRedirect(auth.currentUser, switchProvider);
          return;
        } catch (redirectError) {
          localStorage.removeItem(GOOGLE_LINK_REDIRECT_PENDING_KEY);
          showAlert('연동 실패', `오류: ${redirectError.message}`, 'error');
        }
      } else {
        showAlert('연동 실패', `오류: ${error.message}`, 'error');
      }
    } finally {
      setIsLinking(false);
    }
  };

  const handleUnlinkGoogle = () => {
    setConfirmModal({
      isOpen: true,
      title: '구글 연동 해제',
      message: '구글 계정 연동을 해제하시겠습니까? 구글 로그인과 구글 캘린더 연동이 모두 해제됩니다.',
      type: 'warning',
      confirmText: '해제',
      onConfirm: async () => {
        try {
          const hasGoogleProvider = auth.currentUser?.providerData?.some(p => p.providerId === 'google.com');
          if (hasGoogleProvider) {
            await unlink(auth.currentUser, 'google.com');
          }
          const token = await auth.currentUser.getIdToken();
          await fetch(`${API_BASE_URL}/api/auth/unlink-google`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
          });
          setIsGoogleLinked(false);
          setIsCalendarLinked(false);
          localStorage.setItem('loginMethod', 'local');
          localStorage.setItem('googleConnected', 'false');
          window.dispatchEvent(new Event('userProfileUpdated'));
          showAlert('해제 완료', '구글 계정 연동이 해제되었습니다.', 'success');
        } catch (error) {
          showAlert('해제 실패', `오류: ${error.message}`, 'error');
        }
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleLinkCalendar = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/auth/google/calendar-consent?returnUrl=${encodeURIComponent('/settings')}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.url) window.location.href = data.url;
    } catch (error) {
      showAlert('오류', '구글 캘린더 연동 URL 생성에 실패했습니다.', 'error');
    }
  };

  const handleSyncToGoogle = async () => {
    setIsSyncing(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/calendar/sync-to-google`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      let removedCount = 0;
      try {
        const syncRes = await fetch(`${API_BASE_URL}/api/calendar/sync-from-google`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (syncRes.ok) removedCount = (await syncRes.json()).removed || 0;
      } catch (syncErr) {
        console.warn('역동기화 실패:', syncErr);
      }
      if (res.ok) {
        const parts = [];
        if (data.synced > 0) parts.push(`${data.synced}개 일정 → 구글 동기화`);
        if (data.skipped > 0) parts.push(`${data.skipped}개 중복 스킵`);
        if (removedCount > 0) parts.push(`${removedCount}개 구글에서 삭제된 일정 제거`);
        showAlert('동기화 완료', parts.length > 0 ? parts.join(', ') : '모든 일정이 최신 상태입니다.', parts.length > 0 ? 'success' : 'info');
      } else {
        showAlert('동기화 실패', data.msg || '동기화에 실패했습니다.', 'error');
      }
    } catch (error) {
      showAlert('동기화 실패', `오류: ${error.message}`, 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const loadNotifSettings = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/api/users/profile/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setReminderEnabled(data.reminderEnabled !== false);
        if (Array.isArray(data.reminderMinutesList) && data.reminderMinutesList.length > 0) {
          setReminderMinutesList(data.reminderMinutesList);
        } else {
          setReminderMinutesList([data.reminderMinutes || 10]);
        }
      }
    } catch {}
  }, []);

  useEffect(() => { loadNotifSettings(); }, [loadNotifSettings]);

  const handleSaveNotifSettings = async () => {
    setIsSavingNotif(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/users/profile/notifications`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reminderEnabled, reminderMinutesList }),
      });
      if (res.ok) showAlert('저장 완료', '알림 설정이 저장되었습니다.', 'success');
      else showAlert('저장 실패', '알림 설정 저장에 실패했습니다.', 'error');
    } catch (e) {
      showAlert('저장 실패', e.message, 'error');
    } finally {
      setIsSavingNotif(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const calendarConnected = params.get('calendarConnected');
    const calendarError = params.get('calendarError');
    const pendingSync = localStorage.getItem('pendingCalendarSync');
    if (calendarConnected === 'true' || pendingSync === 'true') {
      localStorage.removeItem('pendingCalendarSync');
      localStorage.setItem('googleConnected', 'true');
      setIsCalendarLinked(true);
      window.history.replaceState({}, document.title, window.location.pathname);
      window.dispatchEvent(new Event('userProfileUpdated'));
    } else if (calendarError) {
      window.history.replaceState({}, document.title, window.location.pathname);
      showAlert('연동 실패', `구글 캘린더 연동에 실패했습니다: ${calendarError}`, 'error');
    }
  }, []);

  // ── 친구 섹션 로직 ──
  const loadFriends = useCallback(async () => {
    setFriendsLoading(true);
    try {
      const data = await friendApi('/api/friends');
      setFriends(data.friends || []);
    } catch (e) {
      console.error('[MobileSettings] loadFriends 오류:', e);
    } finally {
      setFriendsLoading(false);
    }
  }, []);

  useEffect(() => { loadFriends(); }, [loadFriends]);

  // 실시간 친구 알림 소켓
  useEffect(() => {
    const userId = user?._id || user?.id;
    if (!userId) return;
    const socket = io(API_BASE_URL, { transports: ['websocket'], reconnectionAttempts: 3 });
    socket.on('connect', () => socket.emit('join-user', userId));
    socket.on('friend-notif', () => loadFriends());
    return () => socket.disconnect();
  }, [user, loadFriends]);

  // 검색
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await friendApi(`/api/friends/search?q=${encodeURIComponent(searchQuery.trim())}`);
        setSearchResults(Array.isArray(data) ? data : []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const addFriend = async (targetId) => {
    setPendingActions(p => ({ ...p, [targetId]: 'sending' }));
    try {
      const data = await friendApi(`/api/friends/request/${targetId}`, 'POST');
      showToast(data.msg || '친구를 추가했습니다.');
      setPendingActions(p => ({ ...p, [targetId]: 'added' }));
      loadFriends();
    } catch {
      setPendingActions(p => { const n = { ...p }; delete n[targetId]; return n; });
      showToast('오류가 발생했습니다.');
    }
  };

  const deleteFriend = async (friendId) => {
    try {
      await friendApi(`/api/friends/${friendId}`, 'DELETE');
      showToast('친구를 삭제했습니다.');
      loadFriends();
    } catch { showToast('오류가 발생했습니다.'); }
  };

  const isFriend = (id) => friends.some(f => (f._id || f.id) === id);

  if (showPersonalInfo) {
    return <MobilePersonalInfoEdit onBack={() => setShowPersonalInfo(false)} />;
  }

  return (
    <div className="mobile-calendar-view">
      {/* 사이드바 오버레이 */}
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>
      )}

      {/* 사이드바 */}
      <nav className={`mobile-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2 className="sidebar-title">메뉴</h2>
          <button className="sidebar-close-btn" onClick={() => setIsSidebarOpen(false)}>✕</button>
        </div>
        <div className="sidebar-menu">
          <button className="sidebar-item" onClick={() => navigate('/')}>🏠 홈으로</button>
          <button className="sidebar-item" onClick={() => navigate('/schedule')}>📅 내 일정</button>
          <button className="sidebar-item" onClick={() => navigate('/groups')}>👥 그룹</button>
          <button className="sidebar-item" onClick={() => navigate('/calendar')}>📆 달력</button>
          <button className="sidebar-item" onClick={() => setIsSidebarOpen(false)}>⚙️ 설정</button>
        </div>
      </nav>

      {/* 헤더 */}
      <header className="mobile-header">
        <div className="mobile-header-content">
          <div className="mobile-header-left">
            <button className="mobile-menu-btn" onClick={() => setIsSidebarOpen(true)}>
              <Menu size={24} />
            </button>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-800"
            >
              <ChevronLeft size={24} />
            </button>
            <h1 style={{ fontSize: '18px', fontWeight: 700, color: '#1f2937', margin: 0 }}>
              {settingsSection === 'settings' ? '설정' : '친구 관리'}
            </h1>
          </div>
        </div>
      </header>

      {/* 섹션 토글 */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '10px 16px' }}>
        <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '10px', padding: '3px' }}>
          {[
            { key: 'settings', label: '설정' },
            { key: 'friends', label: '친구 관리' },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => setSettingsSection(s.key)}
              style={{
                flex: 1, padding: '8px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                background: settingsSection === s.key ? '#fff' : 'transparent',
                color: settingsSection === s.key ? '#1f2937' : '#6b7280',
                fontWeight: settingsSection === s.key ? 700 : 500,
                fontSize: '14px',
                boxShadow: settingsSection === s.key ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── 설정 섹션 ── */}
      {settingsSection === 'settings' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          {/* 앱 설치 */}
          {installPrompt && (
            <div style={{ background: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe', marginBottom: '16px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>📲</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e40af', marginBottom: '4px' }}>앱으로 설치하기</div>
                  <div style={{ fontSize: '13px', color: '#374151', marginBottom: '10px', lineHeight: '1.4' }}>
                    설치하면 <b>카카오톡·메모 앱에서 공유 → AI Schedule</b>로 바로 일정 추가가 가능합니다.
                  </div>
                  <button
                    onClick={async () => {
                      if (!installPrompt) return;
                      installPrompt.prompt();
                      const { outcome } = await installPrompt.userChoice;
                      if (outcome === 'accepted') onInstallPromptUsed?.();
                    }}
                    style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    앱 설치
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 계정 정보 */}
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '16px', overflow: 'hidden' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #f3f4f6' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1f2937', margin: 0 }}>계정 정보</h3>
            </div>
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>이름</span>
                <span style={{ color: '#1f2937', fontSize: '14px', fontWeight: 500 }}>{user?.firstName || user?.name || ''}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>이메일</span>
                <span style={{ color: '#1f2937', fontSize: '14px', fontWeight: 500 }}>{user?.email}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>로그인 방식</span>
                <span style={{
                  fontSize: '13px', fontWeight: 600, padding: '2px 8px', borderRadius: '9999px',
                  background: isGoogleLinked ? '#dcfce7' : '#dbeafe',
                  color: isGoogleLinked ? '#166534' : '#1e40af'
                }}>
                  {isGoogleLinked ? '구글 + 이메일' : '이메일'}
                </span>
              </div>
              <button
                onClick={() => setShowPersonalInfo(true)}
                style={{ width: '100%', marginTop: '12px', padding: '10px', fontSize: '14px', fontWeight: 600, color: '#3b82f6', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              >
                <UserCog size={16} /> 개인정보 수정
              </button>
            </div>
          </div>

          {/* 구글 계정 연동 */}
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '16px', overflow: 'hidden' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #f3f4f6' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1f2937', margin: 0 }}>구글 계정 연동</h3>
            </div>
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {isGoogleLinked ? <CheckCircle size={20} color="#22c55e" /> : <Link2 size={20} color="#6b7280" />}
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>구글 로그인</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {isGoogleLinked ? '연동됨 - 구글로 로그인 가능' : '미연동 - 이메일로만 로그인 가능'}
                    </div>
                  </div>
                </div>
                {isGoogleLinked ? (
                  <button onClick={handleUnlinkGoogle} style={{ padding: '6px 12px', fontSize: '13px', fontWeight: 600, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Unlink size={14} /> 해제
                  </button>
                ) : (
                  <button onClick={handleLinkGoogle} disabled={isLinking} style={{ padding: '6px 12px', fontSize: '13px', fontWeight: 600, color: 'white', background: isLinking ? '#9ca3af' : '#4285f4', border: 'none', borderRadius: '6px', cursor: isLinking ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Link2 size={14} /> {isLinking ? '연동 중...' : '연동하기'}
                  </button>
                )}
              </div>
              {isGoogleLinked && !isCalendarLinked && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: '#fffbeb', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Calendar size={20} color="#f59e0b" />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>구글 캘린더 미연동</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>캘린더 권한이 필요합니다</div>
                    </div>
                  </div>
                  <button onClick={handleLinkCalendar} style={{ padding: '6px 12px', fontSize: '13px', fontWeight: 600, color: 'white', background: '#22c55e', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={14} /> 연동하기
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 안내 */}
          <div style={{ background: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe', padding: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <AlertCircle size={18} color="#3b82f6" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.5' }}>
                <strong>구글 계정 연동 안내</strong><br />
                - 연동 후 구글 또는 이메일 둘 다로 로그인 가능<br />
                - 기존 일정, 선호시간, 조율방 데이터 모두 유지<br />
                - 구글 캘린더 연동 시 기존 구글 일정이 초록색으로 표시
              </div>
            </div>
          </div>

          {/* 알림 설정 */}
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '16px', overflow: 'hidden' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bell size={18} color="#6b7280" />
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1f2937', margin: 0 }}>알림 설정</h3>
            </div>
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>일정 리마인더</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    {reminderEnabled ? '일정 시작 전 알림 켜짐' : '알림 꺼짐'}
                  </div>
                </div>
                <button
                  onClick={() => setReminderEnabled(v => !v)}
                  style={{ width: '52px', height: '28px', borderRadius: '14px', border: 'none', background: reminderEnabled ? '#3b82f6' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                >
                  <span style={{ position: 'absolute', top: '4px', left: reminderEnabled ? '28px' : '4px', width: '20px', height: '20px', borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </button>
              </div>
              {reminderEnabled && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', letterSpacing: '0.05em', marginBottom: '8px' }}>빠른 선택</div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    {[5, 10, 15, 30].map(min => {
                      const selected = reminderMinutesList.includes(min);
                      const isOnlyOne = reminderMinutesList.length === 1 && selected;
                      return (
                        <button
                          key={min}
                          onClick={() => {
                            if (isOnlyOne) return;
                            setReminderMinutesList(prev =>
                              selected ? prev.filter(m => m !== min) : [...prev, min].sort((a, b) => a - b)
                            );
                          }}
                          style={{ padding: '6px 14px', fontSize: '13px', fontWeight: 600, borderRadius: '20px', border: selected ? '2px solid #3b82f6' : '1.5px solid #e5e7eb', cursor: isOnlyOne ? 'not-allowed' : 'pointer', background: selected ? '#eff6ff' : '#fafafa', color: selected ? '#2563eb' : '#6b7280', opacity: isOnlyOne ? 0.5 : 1, transition: 'all 0.15s' }}
                        >
                          {formatMin(min)} 전
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', letterSpacing: '0.05em', marginBottom: '8px' }}>직접 입력</div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '16px' }}>
                    <input
                      type="number" min={1} value={customMin}
                      onChange={e => setCustomMin(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addCustomMin()}
                      placeholder="숫자 입력"
                      style={{ flex: 1, padding: '8px 10px', fontSize: '14px', textAlign: 'center', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', background: 'white' }}
                    />
                    <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '2px', flexShrink: 0 }}>
                      {['분', '시간'].map(unit => (
                        <button
                          key={unit}
                          onClick={() => setCustomUnit(unit)}
                          style={{ padding: '6px 12px', fontSize: '13px', fontWeight: 600, borderRadius: '6px', border: 'none', cursor: 'pointer', background: customUnit === unit ? 'white' : 'transparent', color: customUnit === unit ? '#1f2937' : '#9ca3af', boxShadow: customUnit === unit ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}
                        >
                          {unit}
                        </button>
                      ))}
                    </div>
                    <button onClick={addCustomMin} style={{ padding: '8px 14px', fontSize: '13px', fontWeight: 700, borderRadius: '8px', border: 'none', cursor: 'pointer', background: '#3b82f6', color: 'white', flexShrink: 0 }}>
                      추가
                    </button>
                  </div>
                  {reminderMinutesList.length > 0 && (
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', letterSpacing: '0.05em', marginBottom: '8px' }}>
                        설정된 알림 ({reminderMinutesList.length}개)
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {[...reminderMinutesList].sort((a, b) => a - b).map(m => (
                          <div key={m} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px 5px 12px', borderRadius: '20px', background: '#2563eb', color: 'white', fontSize: '13px', fontWeight: 600 }}>
                            <span>{formatMin(m)} 전</span>
                            {reminderMinutesList.length > 1 && (
                              <button
                                onClick={() => setReminderMinutesList(prev => prev.filter(x => x !== m))}
                                style={{ width: '16px', height: '16px', borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(255,255,255,0.25)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, padding: 0, lineHeight: 1 }}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={handleSaveNotifSettings}
                disabled={isSavingNotif}
                style={{ width: '100%', padding: '11px', fontSize: '14px', fontWeight: 700, color: 'white', background: isSavingNotif ? '#9ca3af' : '#2563eb', border: 'none', borderRadius: '10px', cursor: isSavingNotif ? 'default' : 'pointer', letterSpacing: '0.02em' }}
              >
                {isSavingNotif ? '저장 중...' : '저장하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 친구 등록 섹션 ── */}
      {settingsSection === 'friends' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* 서브 탭 */}
          <div style={{ display: 'flex', background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
            {[
              { key: 'friends', label: '친구' },
              { key: 'search', label: '검색' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setFriendTab(t.key)}
                style={{
                  flex: 1, padding: '12px 10px', background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '13px', fontWeight: friendTab === t.key ? 700 : 400,
                  color: friendTab === t.key ? '#2563eb' : '#6b7280',
                  borderBottom: friendTab === t.key ? '2px solid #2563eb' : '2px solid transparent',
                  transition: 'all 0.15s', whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* 친구 탭 콘텐츠 */}
          <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '80px' }}>

            {/* 친구 목록 */}
            {friendTab === 'friends' && (
              friendsLoading ? (
                <div style={fCenter}><FriendSpinner /></div>
              ) : friends.length === 0 ? (
                <FriendEmpty icon={<UserCheck size={40} color="#94a3b8" />} text="아직 친구가 없습니다." sub="검색 탭에서 친구를 찾아보세요." />
              ) : (
                <ul style={{ margin: 0, padding: '8px 0', listStyle: 'none' }}>
                  {friends.map(f => (
                    <li key={f._id || f.id} style={fListItem}>
                      <Avatar user={f} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}><UserName user={f} /></div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{f.email}</div>
                      </div>
                      <button onClick={() => deleteFriend(f._id || f.id)} style={fIconBtn} title="친구 삭제">
                        <UserX size={18} color="#ef4444" />
                      </button>
                    </li>
                  ))}
                </ul>
              )
            )}

            {/* 검색 */}
            {friendTab === 'search' && (
              <div style={{ padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '10px 14px', marginBottom: '16px' }}>
                  <Search size={18} color="#94a3b8" />
                  <input
                    type="text"
                    placeholder="닉네임 또는 이메일로 검색"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ border: 'none', outline: 'none', flex: 1, fontSize: '15px', background: 'transparent', color: '#111827' }}
                    autoFocus
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex' }}>
                      <X size={16} color="#94a3b8" />
                    </button>
                  )}
                </div>
                {searching && <div style={fCenter}><FriendSpinner /></div>}
                {!searching && searchQuery && searchResults.length === 0 && (
                  <FriendEmpty icon={<Search size={36} color="#94a3b8" />} text="검색 결과가 없습니다." />
                )}
                {!searching && searchResults.length > 0 && (
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    {searchResults.map(u => {
                      const uid = u._id || u.id;
                      const alreadyFriend = isFriend(uid);
                      const actionState = pendingActions[uid];
                      return (
                        <li key={uid} style={{ ...fListItem, background: '#fff', borderRadius: '12px', marginBottom: '8px', border: '1px solid #f1f5f9' }}>
                          <Avatar user={u} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}><UserName user={u} /></div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{u.email}</div>
                          </div>
                          {alreadyFriend || actionState === 'added' ? (
                            <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: 600, padding: '4px 10px', background: '#dcfce7', borderRadius: '20px' }}>친구</span>
                          ) : (
                            <button
                              onClick={() => addFriend(uid)}
                              disabled={actionState === 'sending'}
                              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: actionState === 'sending' ? 'not-allowed' : 'pointer', opacity: actionState === 'sending' ? 0.6 : 1 }}
                            >
                              <UserPlus size={14} />
                              {actionState === 'sending' ? '...' : '추가'}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 토스트 */}
      {toast && (
        <div style={{ position: 'fixed', bottom: '90px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(17,24,39,0.85)', color: '#fff', padding: '10px 20px', borderRadius: '24px', fontSize: '14px', fontWeight: 500, zIndex: 9999, whiteSpace: 'nowrap', backdropFilter: 'blur(4px)' }}>
          {toast}
        </div>
      )}

      {/* 모달 */}
      <CustomAlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
      <CustomAlertModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        showCancel={true}
        confirmText={confirmModal.confirmText || '확인'}
      />
    </div>
  );
};

export default MobileSettings;
